import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Sparkles, 
  Copy, 
  Check,
  ExternalLink,
  RefreshCw,
  FileText,
  MessageSquare,
  User,
  Heart,
  AlertCircle,
  Book,
  Image,
  X,
  Printer,
  Download
} from 'lucide-react';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { QUESTION_SECTIONS } from '../data/questions';

// Map question ids to their prompt text for friendlier labels (falls back to id)
const QUESTION_LABEL_MAP = Object.fromEntries(
  Object.values(QUESTION_SECTIONS).flatMap(section =>
    (section.questions || []).map(q => [q.id, q.question])
  )
);
function questionLabel(id) {
  return QUESTION_LABEL_MAP[id] || id;
}

export default function SummariesScreen() {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [error, setError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [pendingTranscripts, setPendingTranscripts] = useState([]);
  const [totalResponses, setTotalResponses] = useState(0);
  const [ecpGenerating, setEcpGenerating] = useState(false);
  const [ecpResult, setEcpResult] = useState(null);
  const [quilttGenerating, setQuilttGenerating] = useState(false);
  const [quilttResult, setQuilttResult] = useState(null);
  const [retranscribingId, setRetranscribingId] = useState(null);

  useEffect(() => {
    loadStory();
    loadPhotos();
    loadTranscriptStatus();
  }, [storyId]);
  
  const loadTranscriptStatus = async () => {
    try {
      const responsesRef = collection(db, 'lifeStories', storyId, 'responses');
      const snapshot = await getDocs(responsesRef);
      
      const pending = snapshot.docs.filter(doc => {
        const data = doc.data();
        // Must have audio URL
        if (!data.audioUrl) return false;
        
        // Consider pending if NOT complete
        // (includes: processing, pending, failed, or no status)
        return data.transcriptStatus !== 'complete';
      });
      
      const withAudio = snapshot.docs.filter(doc => doc.data().audioUrl);

      setPendingTranscripts(pending.map(doc => ({
        id: doc.id,
        status: doc.data().transcriptStatus || 'pending'
      })));
      setTotalResponses(withAudio.length);
    } catch (err) {
      console.error('Error checking transcript status:', err);
    }
  };
  
  const loadPhotos = async () => {
    try {
      const contributionsRef = collection(db, 'lifeStories', storyId, 'contributions');
      const q = query(contributionsRef, where('status', '==', 'approved'));
      const snapshot = await getDocs(q);
      
      const approvedPhotos = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(c => c.type === 'photo' && c.photoUrl)
        .sort((a, b) => (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0));
      
      setPhotos(approvedPhotos);
    } catch (err) {
      console.error('Error loading photos:', err);
    }
  };

  const loadStory = async () => {
    try {
      const storyDoc = await getDoc(doc(db, 'lifeStories', storyId));
      if (storyDoc.exists()) {
        setStory({ id: storyDoc.id, ...storyDoc.data() });
      }
      setLoading(false);
    } catch (err) {
      console.error('Error loading story:', err);
      setError('Failed to load story');
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    // Double-check for pending transcripts
    if (pendingTranscripts.length > 0) {
      setError('Please wait for all transcripts to complete before generating summaries.');
      return;
    }
    
    setGenerating(true);
    setError(null);
    
    try {
      const functions = getFunctions();
      const generateAISummaries = httpsCallable(functions, 'generateAISummaries');
      
      const result = await generateAISummaries({ storyId });
      
      // Reload story to get updated summaries
      await loadStory();
      
    } catch (err) {
      console.error('Error generating summaries:', err);
      setError(err.message || 'Failed to generate summaries. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleReprocess = async () => {
    setReprocessing(true);
    setError(null);
    
    try {
      const functions = getFunctions();
      const reprocessTranscripts = httpsCallable(functions, 'reprocessTranscripts', {
        timeout: 300000 // 5 minutes
      });
      
      const result = await reprocessTranscripts({ storyId });
      
      // Reload story and transcript status
      await loadStory();
      await loadTranscriptStatus();
      
      if (result.data.processed > 0) {
        alert(`Transcription complete! ${result.data.processed} recordings processed.`);
      } else {
        alert('All transcripts are already complete.');
      }
      
    } catch (err) {
      console.error('Error reprocessing transcripts:', err);
      
      // Check if it's a timeout error
      if (err.code === 'deadline-exceeded' || err.message?.includes('deadline') || err.message?.includes('timeout')) {
        setError('Transcription is taking longer than expected. It will continue processing in the background. Please refresh the page in a few minutes.');
        // Still refresh status after a delay
        setTimeout(() => {
          loadTranscriptStatus();
        }, 5000);
      } else {
        setError(err.message || 'Failed to reprocess transcripts.');
      }
    } finally {
      setReprocessing(false);
    }
  };

  const handleRetranscribeOne = async (questionId) => {
    setRetranscribingId(questionId);
    setError(null);

    try {
      const functions = getFunctions();
      const retranscribe = httpsCallable(functions, 'retranscribe', {
        timeout: 300000 // 5 minutes
      });

      await retranscribe({ storyId, questionId });
      await loadTranscriptStatus();
      await loadStory();
    } catch (err) {
      console.error('Error re-transcribing response:', err);
      if (err.code === 'deadline-exceeded' || err.message?.includes('deadline') || err.message?.includes('timeout')) {
        setError('Re-transcription is taking longer than expected. It will finish in the background — refresh status in a minute.');
        setTimeout(() => loadTranscriptStatus(), 5000);
      } else {
        setError(err.message || 'Failed to re-transcribe this answer.');
      }
    } finally {
      setRetranscribingId(null);
    }
  };

  const handleQuilttSheet = async () => {
    setQuilttGenerating(true);
    setError(null);
    setQuilttResult(null);

    try {
      const functions = getFunctions();
      const generateQuilttSheet = httpsCallable(functions, 'generateQuilttSheet', {
        timeout: 120000 // 2 minutes — matches the function timeout
      });

      const result = await generateQuilttSheet({ storyId });

      if (result.data?.pdfUrl || result.data?.jsonUrl) {
        setQuilttResult({
          pdfUrl: result.data.pdfUrl || null,
          jsonUrl: result.data.jsonUrl || null,
          capturedCount: result.data.capturedCount,
          totalCount: result.data.totalCount
        });
      } else {
        setError('The Quiltt sheet was generated but no download link was returned. Please try again.');
      }
    } catch (err) {
      console.error('Error generating Quiltt sheet:', err);
      if (err.code === 'functions/failed-precondition') {
        setError('Not enough interview content yet to build a Quiltt sheet. Record more of the life story first.');
      } else {
        setError(err.message || 'Failed to generate the Quiltt sheet. Please try again.');
      }
    } finally {
      setQuilttGenerating(false);
    }
  };

  const handleEcpSheet = async () => {
    setEcpGenerating(true);
    setError(null);
    setEcpResult(null);

    try {
      const functions = getFunctions();
      const generateEcpEntrySheet = httpsCallable(functions, 'generateEcpEntrySheet', {
        timeout: 120000 // 2 minutes — matches the function timeout
      });

      const result = await generateEcpEntrySheet({ storyId });

      if (result.data?.url) {
        setEcpResult({
          url: result.data.url,
          capturedCount: result.data.capturedCount,
          totalCount: result.data.totalCount
        });
        // Open the print-ready sheet in a new tab
        window.open(result.data.url, '_blank', 'noopener,noreferrer');
      } else {
        setError('The ECP sheet was generated but no link was returned. Please try again.');
      }
    } catch (err) {
      console.error('Error generating ECP entry sheet:', err);
      if (err.code === 'functions/failed-precondition') {
        setError('Not enough interview content yet to build an ECP sheet. Record more of the life story first.');
      } else {
        setError(err.message || 'Failed to generate the ECP entry sheet. Please try again.');
      }
    } finally {
      setEcpGenerating(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const residentName = story?.preferredName || story?.residentName || 'Resident';
  const summaries = story?.aiSummaries;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-quiltt-navy border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 -ml-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="font-semibold text-gray-900">AI Summaries</h1>
              <p className="text-sm text-gray-500">{residentName}</p>
            </div>
          </div>
          
          <button
            onClick={handleGenerate}
            disabled={generating || pendingTranscripts.length > 0}
            className="flex items-center gap-2 bg-quiltt-purple hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors"
            title={pendingTranscripts.length > 0 ? 'Wait for transcripts to complete' : ''}
          >
            {generating ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : summaries ? (
              <>
                <RefreshCw className="w-4 h-4" />
                Regenerate
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Generate Summaries
              </>
            )}
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Pending Transcripts Warning */}
        {pendingTranscripts.length > 0 && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
            <RefreshCw className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5 animate-spin" />
            <div className="flex-1">
              <p className="text-amber-800 font-medium">Transcripts Processing</p>
              <p className="text-amber-700 text-sm mb-3">
                {pendingTranscripts.length} of {totalResponses} recordings are still being transcribed.
                Please wait for all transcripts to complete before generating summaries.
              </p>

              {/* Per-response re-transcribe */}
              <ul className="mb-3 space-y-1.5">
                {pendingTranscripts.map(item => (
                  <li
                    key={item.id}
                    className="flex items-center justify-between gap-3 bg-white/70 border border-amber-100 rounded-lg px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="text-sm text-amber-900 truncate">{questionLabel(item.id)}</p>
                      <p className="text-xs text-amber-600">
                        {item.status === 'failed' ? 'Failed' : item.status === 'processing' ? 'Processing…' : 'Pending'}
                      </p>
                    </div>
                    <button
                      onClick={() => handleRetranscribeOne(item.id)}
                      disabled={retranscribingId !== null}
                      className="flex items-center gap-1 text-xs bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 disabled:cursor-not-allowed text-white px-2.5 py-1 rounded flex-shrink-0"
                    >
                      <RefreshCw className={`w-3 h-3 ${retranscribingId === item.id ? 'animate-spin' : ''}`} />
                      {retranscribingId === item.id ? 'Re-transcribing…' : 'Re-transcribe'}
                    </button>
                  </li>
                ))}
              </ul>

              <div className="flex gap-3">
                <button
                  onClick={loadTranscriptStatus}
                  className="text-sm text-amber-600 hover:text-amber-800 underline"
                >
                  Refresh status
                </button>
                <button
                  onClick={handleReprocess}
                  disabled={reprocessing || retranscribingId !== null}
                  className="text-sm bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white px-3 py-1 rounded"
                >
                  {reprocessing ? 'Reprocessing...' : 'Retry All'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {!summaries ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-10 h-10 text-quiltt-navy" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Generate AI Summaries
            </h2>
            <p className="text-gray-600 max-w-md mx-auto mb-6">
              Use AI to create a biography, extract interests, and generate 
              Quiltt Cards and conversation starters from {residentName}'s life story.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <div className="bg-white rounded-lg border p-4 text-left max-w-xs">
                <User className="w-6 h-6 text-quiltt-navy mb-2" />
                <p className="font-medium text-gray-900">About & Interests</p>
                <p className="text-sm text-gray-500">For public profile</p>
              </div>
              <div className="bg-white rounded-lg border p-4 text-left max-w-xs">
                <FileText className="w-6 h-6 text-quiltt-navy mb-2" />
                <p className="font-medium text-gray-900">Quiltt Card</p>
                <p className="text-sm text-gray-500">For staff reference</p>
              </div>
              <div className="bg-white rounded-lg border p-4 text-left max-w-xs">
                <MessageSquare className="w-6 h-6 text-quiltt-navy mb-2" />
                <p className="font-medium text-gray-900">Conversation Starters</p>
                <p className="text-sm text-gray-500">For engagement</p>
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-8">
              {story?.completionPercentage || 0}% of the life story is complete. 
              More content = better summaries.
            </p>
            
            {/* Reprocess button for fixing missing transcripts */}
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl max-w-md mx-auto">
              <p className="text-sm text-amber-800 mb-3">
                <strong>Missing transcripts?</strong> If you recorded content but AI summaries won't generate, 
                click below to reprocess all recordings.
              </p>
              <button
                onClick={handleReprocess}
                disabled={reprocessing}
                className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                {reprocessing ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Reprocessing transcripts...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    Reprocess All Transcripts
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Quiltt Profile Sheet */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="p-5 border-b bg-gray-50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-quiltt-purple" />
                  <h2 className="font-semibold text-gray-900">Quiltt Profile Sheet</h2>
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                    For Staff
                  </span>
                </div>
              </div>
              <div className="p-5">
                <p className="text-sm text-gray-600 mb-4">
                  Generate a print-ready PDF and a JSON export that map {residentName}'s interview to
                  the Quiltt resident-profile fields — for entering (or later importing) the life story
                  into Quiltt. Sensitive care information is intentionally excluded.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={handleQuilttSheet}
                    disabled={quilttGenerating}
                    className="flex items-center gap-2 bg-quiltt-purple hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors"
                  >
                    {quilttGenerating ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Building sheet...
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4" />
                        {quilttResult ? 'Regenerate' : 'Generate Quiltt Sheet'}
                      </>
                    )}
                  </button>
                  {quilttResult?.pdfUrl && (
                    <a
                      href={quilttResult.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-sm text-quiltt-purple hover:underline"
                    >
                      <Download className="w-4 h-4" />
                      Download PDF
                    </a>
                  )}
                  {quilttResult?.jsonUrl && (
                    <a
                      href={quilttResult.jsonUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-sm text-quiltt-purple hover:underline"
                    >
                      <Download className="w-4 h-4" />
                      Download JSON
                    </a>
                  )}
                </div>
                {quilttResult && (
                  <p className="text-xs text-gray-500 mt-3">
                    Captured {quilttResult.capturedCount} of {quilttResult.totalCount} fields from the
                    interview. Download links stay valid for about 2 hours — regenerate for fresh ones.
                  </p>
                )}
              </div>
            </div>

            {/* ECP Data Entry Sheet */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="p-5 border-b bg-gray-50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Printer className="w-5 h-5 text-quiltt-navy" />
                  <h2 className="font-semibold text-gray-900">ECP Data Entry Sheet</h2>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    For Staff
                  </span>
                </div>
              </div>
              <div className="p-5">
                <p className="text-sm text-gray-600 mb-4">
                  Generate a concise, print-ready sheet that maps {residentName}'s interview to ECP's
                  Life Story fields, in ECP's own order — so an assistant can enter everything
                  field-by-field. Sensitive care information is intentionally excluded.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={handleEcpSheet}
                    disabled={ecpGenerating}
                    className="flex items-center gap-2 bg-quiltt-navy hover:bg-quiltt-navy/90 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors"
                  >
                    {ecpGenerating ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Building sheet...
                      </>
                    ) : (
                      <>
                        <Printer className="w-4 h-4" />
                        {ecpResult ? 'Regenerate Sheet' : 'Generate ECP Sheet'}
                      </>
                    )}
                  </button>
                  {ecpResult?.url && (
                    <a
                      href={ecpResult.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-sm text-quiltt-navy hover:underline"
                    >
                      <Download className="w-4 h-4" />
                      Open PDF
                    </a>
                  )}
                </div>
                {ecpResult && (
                  <p className="text-xs text-gray-500 mt-3">
                    Captured {ecpResult.capturedCount} of {ecpResult.totalCount} fields from the
                    interview. Fields not covered appear blank on the sheet for follow-up.
                  </p>
                )}
              </div>
            </div>

            {/* About Section */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="p-5 border-b bg-gray-50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-quiltt-navy" />
                  <h2 className="font-semibold text-gray-900">About</h2>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                    Public Profile
                  </span>
                </div>
                <button
                  onClick={() => copyToClipboard(summaries.about, 'about')}
                  className="flex items-center gap-1 text-sm text-gray-500 hover:text-quiltt-navy"
                >
                  {copiedField === 'about' ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </button>
              </div>
              <div className="p-5">
                <p className="text-gray-700 whitespace-pre-wrap">{summaries.about}</p>
              </div>
            </div>

            {/* Interests Section */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="p-5 border-b bg-gray-50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Heart className="w-5 h-5 text-quiltt-navy" />
                  <h2 className="font-semibold text-gray-900">Interests</h2>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                    Public Profile
                  </span>
                </div>
                <button
                  onClick={() => copyToClipboard(summaries.interests.join(', '), 'interests')}
                  className="flex items-center gap-1 text-sm text-gray-500 hover:text-quiltt-navy"
                >
                  {copiedField === 'interests' ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </button>
              </div>
              <div className="p-5">
                <div className="flex flex-wrap gap-2">
                  {summaries.interests.map((interest, index) => (
                    <span 
                      key={index}
                      className="bg-quiltt-50 text-quiltt-navy px-3 py-1.5 rounded-full text-sm"
                    >
                      {interest}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Quiltt Card Link */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="p-5 border-b bg-gray-50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-quiltt-navy" />
                  <h2 className="font-semibold text-gray-900">Quiltt Card</h2>
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                    Internal Notes
                  </span>
                </div>
              </div>
              <div className="p-5">
                <p className="text-gray-600 mb-4">
                  A comprehensive reference for staff with background, family info, 
                  preferences, communication tips, and important dates.
                </p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href={summaries.careCardUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-quiltt-purple hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View Quiltt Card
                  </a>
                  <button
                    onClick={() => copyToClipboard(`[Quiltt Card](${summaries.careCardUrl})`, 'careCardUrl')}
                    className="flex items-center gap-2 border border-gray-300 hover:border-gray-400 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors"
                  >
                    {copiedField === 'careCardUrl' ? (
                      <>
                        <Check className="w-4 h-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy Link
                      </>
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Paste into Quiltt resident profile notes — it will be clickable in the app.
                </p>
              </div>
            </div>

            {/* Conversation Starters Link */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="p-5 border-b bg-gray-50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <MessageSquare className="w-5 h-5 text-quiltt-navy" />
                  <h2 className="font-semibold text-gray-900">Conversation Starters</h2>
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                    Internal Notes
                  </span>
                </div>
              </div>
              <div className="p-5">
                <p className="text-gray-600 mb-4">
                  {summaries.conversationStarters?.length || 0} personalized questions 
                  and topics to help staff engage in meaningful conversations.
                </p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href={summaries.conversationStartersUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-quiltt-purple hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View Conversation Starters
                  </a>
                  <button
                    onClick={() => copyToClipboard(`[Conversation Starters](${summaries.conversationStartersUrl})`, 'convUrl')}
                    className="flex items-center gap-2 border border-gray-300 hover:border-gray-400 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors"
                  >
                    {copiedField === 'convUrl' ? (
                      <>
                        <Check className="w-4 h-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy Link
                      </>
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Paste into Quiltt resident profile notes — it will be clickable in the app.
                </p>
              </div>
            </div>

            {/* Joy & Purpose */}
            {summaries.joyAndPurpose && (summaries.joyAndPurpose.joyTriggers?.length > 0 || summaries.joyAndPurpose.purposeDrivers?.length > 0) && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="p-5 border-b bg-gray-50 flex items-center gap-3">
                  <span className="text-xl">✨</span>
                  <h2 className="font-semibold text-gray-900">Joy & Purpose</h2>
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                    Activity Insights
                  </span>
                </div>
                <div className="p-5 space-y-6">
                  {/* Joy Triggers */}
                  {summaries.joyAndPurpose.joyTriggers?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-lg">☀️</span>
                        <h3 className="font-medium text-gray-900">What Brings Joy</h3>
                      </div>
                      <div className="space-y-2">
                        {summaries.joyAndPurpose.joyTriggers.map((trigger, i) => (
                          <div key={i} className="flex items-start gap-3 p-3 bg-amber-50/50 rounded-lg">
                            <span className="bg-amber-100 text-amber-700 text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0 mt-0.5">
                              {trigger.theme}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-700">{trigger.detail}</p>
                              {trigger.activityTypes?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1.5">
                                  {trigger.activityTypes.map((type, j) => (
                                    <span key={j} className="text-xs text-amber-600 bg-amber-100/60 px-2 py-0.5 rounded">
                                      {type}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Purpose Drivers */}
                  {summaries.joyAndPurpose.purposeDrivers?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-lg">🎯</span>
                        <h3 className="font-medium text-gray-900">What Gives Purpose</h3>
                      </div>
                      <div className="space-y-2">
                        {summaries.joyAndPurpose.purposeDrivers.map((driver, i) => (
                          <div key={i} className="flex items-start gap-3 p-3 bg-blue-50/50 rounded-lg">
                            <span className="bg-blue-100 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0 mt-0.5">
                              {driver.theme}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-700">{driver.detail}</p>
                              {driver.activityTypes?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1.5">
                                  {driver.activityTypes.map((type, j) => (
                                    <span key={j} className="text-xs text-blue-600 bg-blue-100/60 px-2 py-0.5 rounded">
                                      {type}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Social & Sensory Notes */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {summaries.joyAndPurpose.socialPreferences && (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Social Preferences</p>
                        <p className="text-sm text-gray-700">{summaries.joyAndPurpose.socialPreferences}</p>
                      </div>
                    )}
                    {summaries.joyAndPurpose.sensoryNotes && (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Sensory Notes</p>
                        <p className="text-sm text-gray-700">{summaries.joyAndPurpose.sensoryNotes}</p>
                      </div>
                    )}
                  </div>

                  {summaries.joyAndPurpose.avoidances && (
                    <div className="p-3 bg-red-50/50 rounded-lg">
                      <p className="text-xs font-medium text-red-400 uppercase tracking-wide mb-1">Prefers to Avoid</p>
                      <p className="text-sm text-gray-700">{summaries.joyAndPurpose.avoidances}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Photo Gallery */}
            {photos.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-pink-50 rounded-xl flex items-center justify-center">
                    <Image className="w-5 h-5 text-pink-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Family Photos</h3>
                    <p className="text-sm text-gray-500">{photos.length} photo{photos.length !== 1 ? 's' : ''} from family contributions</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                  {photos.map((photo) => (
                    <button
                      key={photo.id}
                      onClick={() => setSelectedPhoto(photo)}
                      className="aspect-square rounded-lg overflow-hidden hover:ring-2 hover:ring-quiltt-purple transition-all"
                    >
                      <img 
                        src={photo.photoUrl} 
                        alt={photo.caption || 'Family photo'}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Print Shop CTA */}
            <div className="bg-gradient-to-r from-quiltt-purple to-purple-600 rounded-2xl p-6 text-white">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  <Book className="w-7 h-7" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">Create a Printed Book</h3>
                  <p className="text-white/80 text-sm mt-1">
                    Turn this life story into a beautiful keepsake book for family and loved ones.
                  </p>
                </div>
                <button
                  onClick={() => navigate(`/story/${storyId}/print`)}
                  className="px-5 py-2.5 bg-white text-quiltt-purple font-medium rounded-lg hover:bg-white/90 transition-colors flex-shrink-0"
                >
                  Order Book
                </button>
              </div>
            </div>

            {/* Last generated */}
            {summaries.generatedAt && (
              <p className="text-center text-sm text-gray-500">
                Generated {summaries.generatedAt.toDate?.()?.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                }) || 'recently'}
              </p>
            )}
          </div>
        )}
      </main>
      
      {/* Photo Lightbox Modal */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <button
            onClick={() => setSelectedPhoto(null)}
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white transition-colors"
          >
            <X className="w-8 h-8" />
          </button>
          <div 
            className="max-w-4xl max-h-[90vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={selectedPhoto.photoUrl} 
              alt={selectedPhoto.caption || 'Family photo'}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            {(selectedPhoto.caption || selectedPhoto.contributorName) && (
              <div className="mt-4 text-center text-white">
                {selectedPhoto.caption && (
                  <p className="text-lg">{selectedPhoto.caption}</p>
                )}
                {selectedPhoto.contributorName && (
                  <p className="text-sm text-white/70 mt-1">
                    Shared by {selectedPhoto.contributorName}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
